import { beginViewerSwap, endViewerSwap } from 'Pods/ThreeDStudio/Store/actions/staticViewer'

/**
 * Pausing the render loop leaves the last presented frame on the canvas - checked
 * on both the webgpu and webgl backends. Freezing it before `unloadAllModels`
 * means a model switch never exposes an empty viewport: the outgoing model stays
 * on screen until the incoming one is loaded and configured, then one `resume`
 * swaps the two on the next frame. The loader still shows over it, rendered
 * see-through so the held model reads through the overlay rather than being
 * hidden behind a blank panel.
 *
 * `viewer.isPaused` doubles as the frozen flag. Nothing else in the studio pauses
 * the viewer, so a paused loop can only mean a swap this module started - which
 * makes a second switch arriving mid-swap reuse the frame already frozen instead
 * of pausing twice, and keeps unfreeze idempotent.
 *
 * @returns {boolean} whether the frame is now frozen; false means the viewport is
 *   about to go empty, so the loader must render opaque instead of see-through
 */
export const freezeFrame = (viewer) => {
  if (!viewer || viewer.isDisposed) {
    return false
  }

  if (viewer.isPaused) {
    return true
  }

  try {
    // Read before the unload zeroes the counts, so the details card can hold the
    // outgoing model's numbers rather than collapsing halfway through the swap.
    const frozenStats = viewer.getStats()
    viewer.pause()
    beginViewerSwap(frozenStats)

    return true
  } catch {
    return false
  }
}

/**
 * Resumes the render loop and clears the swap flag. Safe on every path that ends
 * a load, including failures and teardown, and a no-op when nothing was frozen.
 * Every terminal path MUST call it - a frame left frozen shows a model the viewer
 * no longer holds, with camera input dead.
 */
export const unfreezeFrame = (viewer) => {
  endViewerSwap()

  if (!viewer || viewer.isDisposed || !viewer.isPaused) {
    return
  }

  try {
    viewer.resume()
  } catch {
    // The viewer is going away; there is no frame left to reveal.
  }
}
